// ---------------------------------------------------------------------------
// LockScreen — unlock form for returning users
// ---------------------------------------------------------------------------

import { useState, useEffect, useRef, type FormEvent } from 'react';

interface LockScreenProps {
  onUnlock: (password: string) => Promise<boolean>;
}

export function LockScreen({ onUnlock }: LockScreenProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const ok = await onUnlock(password);
      if (!ok) {
        setError('Incorrect password. Please try again.');
        setPassword('');
        setSubmitting(false);
      }
      // On success the gating flips to the main app and this screen unmounts.
    } catch (err) {
      console.error('Unlock failed:', err);
      setError('Failed to unlock. Please try again.');
      setPassword('');
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)] px-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-surface p-8 shadow-lg">
        <div className="mb-6 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-white">
            <svg
              className="h-7 w-7"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
        </div>

        <h1 className="mb-2 text-center text-xl font-bold text-[var(--color-text)]">
          App locked
        </h1>
        <p className="mb-6 text-center text-sm text-[var(--color-text-secondary)]">
          Enter your password to unlock GPA Scales and decrypt your data.
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            ref={inputRef}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-[var(--color-text)] focus:border-border-focus focus:outline-none"
            placeholder="Password"
            aria-label="Password"
            disabled={submitting}
          />

          {error && (
            <p className="text-xs text-error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || !password}
            className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-[var(--transition-fast)] hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Unlocking…' : 'Unlock'}
          </button>
        </form>
      </div>
    </div>
  );
}
